// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

/**
 * What a guide pick is, for the lab surfaces: its comparison numbers, whether it is the product
 * the reader declared, and whether a surface draws its curves at all. Lifted out of
 * `GuidePick.tsx` (2026-10-18) so the desktop panel (`LabCompareDrawer` → `PickFocusBody`) reads
 * a pick the way the row does, without importing the row.
 */
import type { GuidePickContext } from 'lib/dealhunt/types'
import type { DeclaredProduct } from './declared'
import { sameProduct, type DeltaNumbers } from './deltas'
import type { DSGuidePickRef } from 'types.sg.ds'

/**
 * The pick's comparison numbers. Price and the SG score are deliberately absent: they are never
 * a comparison row (Constraint `f80d2e70`), and the card already carries both. `clearbuyProductId`
 * falls back to the article's own ClearBuy id so the lab tier — sparklines, the dialog, the
 * drawer — works before DealHunt ships `/api/v1/sg/guide-picks` and `context` is null.
 */
export function pickNumbers(pick: DSGuidePickRef): DeltaNumbers {
  const compare = pick.context?.compare ?? null
  return {
    axes: compare?.axes ?? null,
    batteryHours: compare?.batteryHours ?? null,
    priceCents: null,
    sgScore: null,
    clearbuyProductId: compare?.clearbuyProductId ?? pick.clearBuyProductId,
    compareSpecs: compare?.compareSpecs ?? null,
  }
}

/**
 * Is this pick the product the reader declared? Through the alias group first (#110 — a node has
 * siblings, so never `===` on one id), then the slugs, and finally the ClearBuy id, which is the
 * only join there is while `context` is null.
 */
export function isDeclaredPick(
  declared: DeclaredProduct,
  context: GuidePickContext | null,
  clearbuyProductId: number | null
): boolean {
  if (context && declared.nodeId) {
    if (context.node_id === declared.nodeId || context.node_ids.includes(declared.nodeId)) {
      return true
    }
  }
  if (declared.slug && context?.full_slug) {
    return sameProduct(
      { nodeId: declared.nodeId, slug: declared.slug },
      { nodeId: context.node_id, slug: context.full_slug }
    )
  }
  return (
    typeof declared.clearbuyProductId === 'number' &&
    clearbuyProductId !== null &&
    declared.clearbuyProductId === clearbuyProductId
  )
}

/**
 * Does this surface draw the pick's curves at all? Declared: always — the lab tier works off the
 * ClearBuy id even when DealHunt could not resolve the pick. Undeclared, and for the pick the
 * reader owns (its curves alone, nothing to overlay): only when the contract says the lab
 * measured it (2026-09-16), so nine rows never fetch to learn there is nothing to draw, and a
 * pick with no context keeps the block it had (the chips, no chart).
 */
export function wantsCurves(
  declared: DeclaredProduct | null,
  yours: boolean,
  context: GuidePickContext | null
): boolean {
  if (yours) {
    return context?.lab_tested === true
  }
  return declared !== null || context?.lab_tested === true
}

/** The pick the reader declared, by the same join the row uses. */
export function pickIsYours(pick: DSGuidePickRef, declared: DeclaredProduct | null): boolean {
  return (
    declared !== null &&
    isDeclaredPick(
      declared,
      pick.context,
      pick.context?.compare.clearbuyProductId ?? pick.clearBuyProductId
    )
  )
}
